import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Group, Burger, Drawer, Stack, Anchor, Menu, Box } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import logo from '../../assets/logo_transparent.png';
import pointeShoeIcon from '../../assets/pointe_shoe_icon.png';
import './Header.css'; // We'll create a small CSS file for responsive behavior
import { getActiveSeason } from '../../config/seasons';

const Header = () => {
  const [mobileOpened, { toggle: toggleMobile, close: closeMobile }] = useDisclosure(false);
  const [expandedIdx, setExpandedIdx] = React.useState(null);
  const location = useLocation();

  // Reset competition expansion when drawer closes
  React.useEffect(() => {
    if (!mobileOpened) {
      setExpandedIdx(null);
    }
  }, [mobileOpened]);

  const isActive = (path) => location.pathname === path;

  const seasonYear = getActiveSeason().year;

  // Navigation links data
  const navLinks = [
    { to: '/', label: 'Home' },
    // Named after the season people are signing up for; the year rolls over
    // with SEASONS, so nothing here needs editing next year.
    {
      label: `${seasonYear} Competition`,
      to: '/register',
      dropdown: [
        { to: '/competition', label: 'Our Philosophy' },
        { to: '/register', label: `Register for ${seasonYear}` },
        { to: '/competition-rules', label: `${seasonYear} Rules & Fees` },
        { to: '/faq', label: 'FAQ' },
      ],
    },
    // Past seasons live in their own branch so the primary nav never becomes a
    // year picker. Adding 2027 is one line here plus one page, rather than
    // renaming "2026 Competition Info" and editing prose in six files.
    {
      label: 'Past Seasons',
      to: '/2026',
      dropdown: [
        { to: '/2026', label: '2026 Results & Gallery' },
      ],
    },
    { to: '/support', label: 'Support' },
    { to: '/learn-explore', label: 'Learn & Explore' },
    { to: '/contact', label: 'Contact' },
  ];

  // Profile link (right-aligned)
  const profileLink = {
    label: 'My Profile',
    to: '/profile',
  };

  // Desktop navigation link component
  const DesktopNavLink = ({ to, label, active }) => (
    <Anchor
      component={Link}
      to={to}
      className={`header-nav-link ${active ? 'active' : ''}`}
    >
      {label}
    </Anchor>
  );

  // Desktop dropdown component
  const DesktopDropdown = ({ link }) => (
    <Menu
      trigger="hover"
      openDelay={0}
      closeDelay={200}
      position="bottom-start"
      offset={0}
      classNames={{
        dropdown: 'header-dropdown',
        item: 'header-dropdown-item',
      }}
    >
      <Menu.Target>
        <Box component="span" className="header-dropdown-trigger">
          <Anchor
            component={Link}
            to={link.to}
            className={`header-nav-link ${isActive(link.to) ? 'active' : ''}`}
          >
            <span>{link.label}</span>
            <span className="dropdown-arrow">▼</span>
          </Anchor>
        </Box>
      </Menu.Target>

      <Menu.Dropdown>
        {link.dropdown.map((item, idx) => (
          <Menu.Item key={idx} component={Link} to={item.to}>
            {item.label}
          </Menu.Item>
        ))}
      </Menu.Dropdown>
    </Menu>
  );

  // Mobile navigation link component
  const MobileNavLink = ({ to, label, onClick }) => (
    <Link
      to={to}
      onClick={onClick}
      className={`mobile-nav-link ${isActive(to) ? 'active' : ''}`}
    >
      {label}
    </Link>
  );

  // Profile link (desktop)
  const ProfileLink = () => (
    <DesktopNavLink
      to={profileLink.to}
      label={profileLink.label}
      active={isActive(profileLink.to)}
    />
  );

  return (
    <>
      {/* Desktop/Mobile Header */}
      <Box component="header" className="app-header">
        {/* Logo */}
        <Link to="/">
          <img
            src={logo}
            alt="Prix du Canada Logo"
            className="header-logo"
          />
        </Link>

        {/* Desktop Navigation */}
        <Group gap="2.5rem" wrap="nowrap" className="desktop-nav">
          {navLinks.map((link, idx) =>
            link.dropdown ? (
              <DesktopDropdown key={idx} link={link} />
            ) : (
              <DesktopNavLink
                key={idx}
                to={link.to}
                label={link.label}
                active={isActive(link.to)}
              />
            )
          )}

          {/* Profile with Icon */}
          <Group gap="0.5rem" className="profile-group">
            <img src={pointeShoeIcon} alt="" className="profile-icon" />
            <ProfileLink />
          </Group>
        </Group>

        {/* Mobile Hamburger */}
        <Burger
          opened={mobileOpened}
          onClick={toggleMobile}
          color="white"
          size="md"
          className="mobile-burger"
        />
      </Box>

      {/* Mobile Drawer Menu */}
      <Drawer
        opened={mobileOpened}
        onClose={closeMobile}
        position="right"
        size="100%"
        withCloseButton={true}
        closeButtonProps={{
          size: 'xl',
          iconSize: 30,
          style: { color: 'white' }
        }}
        classNames={{
          body: 'mobile-drawer-body',
          content: 'mobile-drawer-content',
          close: 'mobile-drawer-close',
        }}
        transitionProps={{
          transition: 'slide-left',
          duration: 300,
        }}
      >
        <Stack gap="0" className="mobile-nav-stack">
          {navLinks.map((link, idx) =>
            link.dropdown ? (
              <Box key={idx} className="mobile-dropdown">
                <div
                  className="mobile-nav-link"
                  onClick={() => setExpandedIdx(expandedIdx === idx ? null : idx)}
                  style={{ cursor: 'pointer' }}
                >
                  {link.label} <span className="mobile-expand-icon">▼</span>
                </div>
                {expandedIdx === idx && (
                  <Box className="mobile-dropdown-content">
                    {link.dropdown.map((item, subIdx) => (
                      <MobileNavLink
                        key={subIdx}
                        to={item.to}
                        label={item.label}
                        onClick={() => {
                          setExpandedIdx(null);
                          closeMobile();
                        }}
                      />
                    ))}
                  </Box>
                )}
              </Box>
            ) : (
              <MobileNavLink
                key={idx}
                to={link.to}
                label={link.label}
                onClick={closeMobile}
              />
            )
          )}

          {/* Profile Link (Mobile) */}
          <MobileNavLink
            to={profileLink.to}
            label={profileLink.label}
            onClick={closeMobile}
          />
        </Stack>
      </Drawer>
    </>
  );
};

export default Header;
